// LipSyncController.minimal.js (UPGRADED: coarticulation + jaw overlay + performance caching)
// - Keeps your protected emotion shapes untouched.
// - Stops doing full "reset all shapes" every frame. Instead, zeros ONLY the shapes used by phonemes.
// - Supports MOUTH as a separate overlay channel (jaw openness) without killing the active phoneme.
// - Adds gentle coarticulation (previous phoneme decays instead of instant hard-zero).
// - Works with either model.morphTargetDictionary or blendShapeControls.

import { BlendShapeMapper } from "../BlendShapeMapper.js";

export class LipSyncController {
  constructor(opts) {
    opts = opts || {};
    this.model = opts.model === undefined ? null : opts.model;
    this.blendShapeControls =
      opts.blendShapeControls === undefined ? null : opts.blendShapeControls;
    this.debug = opts.debug === undefined ? true : !!opts.debug;

    this.attackMs = opts.attackMs === undefined ? 55 : opts.attackMs;
    this.releaseMs = opts.releaseMs === undefined ? 125 : opts.releaseMs;
    this.frameMs = opts.frameMs === undefined ? 33 : opts.frameMs;

    // Coarticulation controls
    this.coarticulation =
      opts.coarticulation === undefined ? 0.18 : +opts.coarticulation; // 0..0.35
    this.minHoldMs = opts.minHoldMs === undefined ? 45 : +opts.minHoldMs;

    this.mapper = new BlendShapeMapper();
    this.supportedPhonemes = Object.keys(this.mapper.standardPhonemes);
    if (this.supportedPhonemes.indexOf("MOUTH") === -1)
      this.supportedPhonemes.push("MOUTH");

    this.defaultIntensity = 0.55;

    // State per viseme: { w, target, lastSetAt }
    this.visemeState = new Map();

    // Cache: only touch mapped phoneme targets (performance + safety)
    this._cache = {
      mode: null, // "model" | "controls"
      key: null, // model ref or controls ref
      availableNames: null,
      targetNames: null, // Set<string> of all phoneme-related morph names
    };

    this._animating = false;
    this._tick = this._tick.bind(this);

    if (this.debug) {
      console.log("[LipSync] init", {
        hasModel: !!this.model,
        hasBlendShapeControls: !!this.blendShapeControls,
        supportedPhonemes: this.supportedPhonemes,
        attackMs: this.attackMs,
        releaseMs: this.releaseMs,
        coarticulation: this.coarticulation,
      });
    }
  }

  setModel(m) {
    this.model = m;
    this._invalidateCache();
    if (this.debug)
      console.log("[LipSync] setModel()", { hasModel: !!this.model });
  }

  setBlendShapeControls(c) {
    this.blendShapeControls = c;
    this._invalidateCache();
    if (this.debug) {
      const keys = c ? Object.keys(c) : [];
      console.log("[LipSync] setBlendShapeControls()", {
        count: keys.length,
        sample: keys.slice(0, 8),
      });
    }
  }

  getSupportedPhonemes() {
    return this.supportedPhonemes.slice();
  }
  setFrameMs(ms) {
    this.frameMs = ms;
  }

  _invalidateCache() {
    this._cache.mode = null;
    this._cache.key = null;
    this._cache.availableNames = null;
    this._cache.targetNames = null;
  }

  _ensureCache() {
    // Decide mode
    if (
      this.model &&
      this.model.morphTargetDictionary &&
      this.model.morphTargetInfluences
    ) {
      if (
        this._cache.mode === "model" &&
        this._cache.key === this.model &&
        this._cache.targetNames
      )
        return;

      const available = Object.keys(this.model.morphTargetDictionary);
      const targetNames = this._collectAllPhonemeTargetNames(available);

      this._cache.mode = "model";
      this._cache.key = this.model;
      this._cache.availableNames = available;
      this._cache.targetNames = targetNames;

      if (this.debug) {
        console.log("[LipSync] cache(model) ready", {
          available: available.length,
          phonemeTargets: targetNames.size,
        });
      }
      return;
    }

    if (this.blendShapeControls) {
      if (
        this._cache.mode === "controls" &&
        this._cache.key === this.blendShapeControls &&
        this._cache.targetNames
      )
        return;

      const available = Object.keys(this.blendShapeControls);
      const targetNames = this._collectAllPhonemeTargetNames(available);

      this._cache.mode = "controls";
      this._cache.key = this.blendShapeControls;
      this._cache.availableNames = available;
      this._cache.targetNames = targetNames;

      if (this.debug) {
        console.log("[LipSync] cache(controls) ready", {
          available: available.length,
          phonemeTargets: targetNames.size,
        });
      }
      return;
    }

    // no target
    this._cache.mode = null;
    this._cache.key = null;
    this._cache.availableNames = null;
    this._cache.targetNames = null;
  }

  // Broad reset (zeros all non-protected shapes). Kept for compatibility.
  reset() {
    this.visemeState.clear();

    if (
      this.model &&
      this.model.morphTargetDictionary &&
      this.model.morphTargetInfluences
    ) {
      if (this.debug) console.log("[LipSync] reset(): via model (full reset)");
      this.mapper.resetBlendShapes(this.model);
      return;
    }

    if (this.blendShapeControls) {
      if (this.debug)
        console.log("[LipSync] reset(): via blendShapeControls (full reset)");
      const entries = Object.entries(this.blendShapeControls);
      for (let i = 0; i < entries.length; i++) {
        const name = entries[i][0],
          raw = entries[i][1];
        if (!this.mapper.isProtectedEmotionShape(name)) {
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
          for (const control of items) {
            if (control && control.mesh && typeof control.index === "number") {
              control.mesh.morphTargetInfluences[control.index] = 0;
              control.value = 0;
            }
          }
        }
      }
      return;
    }

    console.warn("[LipSync] reset(): no target set");
  }

  // NEW: Phoneme-only hard zero (optionally keep MOUTH for jaw overlay use-cases)
  zeroAllPhonemes(opts) {
    opts = opts || {};
    const keepMouth = !!opts.keepMouth;

    try {
      this._ensureCache();
      this.visemeState.clear();

      if (this._cache.mode === "model") {
        const set = this._cache.targetNames;
        if (!set) return;

        for (const name of set) {
          if (keepMouth && this._isMouthTargetName(name)) continue;
          const idx = this.model.morphTargetDictionary[name];
          if (typeof idx === "number")
            this.model.morphTargetInfluences[idx] = 0;
        }
        return;
      }

      if (this._cache.mode === "controls") {
        const set = this._cache.targetNames;
        if (!set) return;

        for (const name of set) {
          if (keepMouth && this._isMouthTargetName(name)) continue;
          const raw = this.blendShapeControls[name];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
          for (const ctl of items) {
            if (ctl && ctl.mesh && typeof ctl.index === "number") {
              ctl.mesh.morphTargetInfluences[ctl.index] = 0;
              ctl.value = 0;
            }
          }
        }
        return;
      }

      console.warn("[LipSync] zeroAllPhonemes(): no target set");
    } catch (e) {
      console.warn("[LipSync] zeroAllPhonemes() failed", e);
    }
  }

  _isMouthTargetName(name) {
    // crude but safe: if it's one of the jaw/mouth-open names in MOUTH list, treat as mouth driver
    const list = this.mapper.standardPhonemes.MOUTH || [];
    const n = String(name);
    for (let i = 0; i < list.length; i++) {
      if (n.indexOf(list[i]) !== -1) return true;
    }
    return false;
  }

  // Helper: gather all mapped target names for our phoneme set
  _collectAllPhonemeTargetNames(availableNames) {
    const out = new Set();
    if (!Array.isArray(availableNames) || availableNames.length === 0)
      return out;

    const parts = ["head", "mouth", "beard", "moustache"];
    for (let p = 0; p < parts.length; p++) {
      for (let i = 0; i < this.supportedPhonemes.length; i++) {
        const ph = this.supportedPhonemes[i];
        const mapped = this.mapper.mapPhonemeToBlendShapes(
          availableNames,
          ph,
          parts[p],
        );
        for (let j = 0; j < mapped.length; j++) {
          const nm = String(mapped[j].name);
          if (!this.mapper.isProtectedEmotionShape(nm)) out.add(nm);
        }
      }
    }

    // If mapping found nothing (rare), fall back to a conservative regex
    if (out.size === 0) {
      const re =
        /(viseme|phoneme|mouth|lip|jaw|AA|AE|AH|AO|EH|ER|IH|IY|UH|UW|MBP|FV|L_ER|R|S|SH|TH|CH|JH|D|T|K|G|P|B|M|N|NG|OO|iee|ohh|uuu|rrr|www|sss|fff|tldn)/i;
      for (const key of availableNames) {
        if (re.test(key) && !this.mapper.isProtectedEmotionShape(key))
          out.add(key);
      }
    }
    return out;
  }

  applyPhoneme(phoneme, intensity) {
    intensity = intensity === undefined ? this.defaultIntensity : intensity;

    if (!phoneme || this.supportedPhonemes.indexOf(phoneme) === -1) {
      if (this.debug)
        console.warn('[LipSync] Unsupported phoneme "' + phoneme + '".');
      return;
    }

    // clamp & shape intensity slightly (more natural low-level motion)
    let t = Math.min(1, Math.max(0, +intensity));
    t = Math.pow(t, 0.85);

    const now = performance.now();

    if (this.debug) {
      console.log("[LipSync] applyPhoneme()", {
        phoneme,
        intensity: Number(t).toFixed(3),
      });
    }

    if (!this.visemeState.has(phoneme)) {
      this.visemeState.set(phoneme, { w: 0, target: t, lastSetAt: now });
    } else {
      const st = this.visemeState.get(phoneme);
      st.target = t;
      st.lastSetAt = now;
    }

    // Coarticulation:
    // - If phoneme is MOUTH, we DON'T force others to zero (it's an overlay).
    // - Otherwise, we decay others instead of instant target=0.
    if (phoneme !== "MOUTH") {
      const it = this.visemeState.entries();
      for (let e = it.next(); !e.done; e = it.next()) {
        const key = e.value[0],
          st = e.value[1];
        if (key === phoneme) continue;
        if (key === "MOUTH") continue; // overlay stays as set by caller

        // hold for a tiny moment to reduce jitter, then decay
        const age = now - (st.lastSetAt || 0);
        if (age < this.minHoldMs) continue;

        st.target = Math.max(0, st.target * this.coarticulation);
      }
    }

    if (!this._animating) {
      this._animating = true;
      requestAnimationFrame(this._tick);
    }
  }

  applySequenceImmediate(seq, intensity) {
    if (!Array.isArray(seq) || seq.length === 0) return;
    this.applyPhoneme(
      seq[seq.length - 1],
      intensity === undefined ? this.defaultIntensity : intensity,
    );
  }

  _tick() {
    const kAttack = 1 - Math.exp(-this.frameMs / this.attackMs);
    const kRelease = 1 - Math.exp(-this.frameMs / this.releaseMs);

    let any = false;
    const toDel = [];
    const it = this.visemeState.entries();

    for (let e = it.next(); !e.done; e = it.next()) {
      const key = e.value[0],
        st = e.value[1];
      const k = st.target > st.w ? kAttack : kRelease;
      st.w += (st.target - st.w) * k;

      if (st.w > 0.001 || st.target > 0) any = true;
      else toDel.push(key);
    }

    for (let i = 0; i < toDel.length; i++) this.visemeState.delete(toDel[i]);

    this._renderVisemes();

    if (any) requestAnimationFrame(this._tick);
    else this._animating = false;
  }

  _zeroCachedTargetsModel(keepMouth) {
    const set = this._cache.targetNames;
    if (!set) return;
    for (const name of set) {
      if (keepMouth && this._isMouthTargetName(name)) continue;
      const idx = this.model.morphTargetDictionary[name];
      if (
        typeof idx === "number" &&
        !this.mapper.isProtectedEmotionShape(name)
      ) {
        this.model.morphTargetInfluences[idx] = 0;
      }
    }
  }

  _zeroCachedTargetsControls(keepMouth) {
    const set = this._cache.targetNames;
    if (!set) return;
    for (const name of set) {
      if (keepMouth && this._isMouthTargetName(name)) continue;
      if (this.mapper.isProtectedEmotionShape(name)) continue;
      const raw = this.blendShapeControls[name];
      const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
      for (const ctl of items) {
        if (ctl && ctl.mesh && typeof ctl.index === "number") {
          ctl.mesh.morphTargetInfluences[ctl.index] = 0;
          ctl.value = 0;
        }
      }
    }
  }

  _renderVisemes() {
    this._ensureCache();

    // If we have a MOUTH overlay active, we should not zero it when clearing others,
    // otherwise jaw-drive will fight with resets.
    const mouthSt = this.visemeState.get("MOUTH");
    const keepMouth = !!(mouthSt && (mouthSt.w > 0.001 || mouthSt.target > 0));

    if (this._cache.mode === "model") {
      this._zeroCachedTargetsModel(keepMouth);

      const it = this.visemeState.entries();
      for (let e = it.next(); !e.done; e = it.next()) {
        const viseme = e.value[0],
          st = e.value[1];
        if (st.w <= 0) continue;
        this.mapper.applyPhonemeBlendShape(this.model, viseme, st.w);
      }
      return;
    }

    if (this._cache.mode === "controls") {
      this._zeroCachedTargetsControls(keepMouth);

      const available =
        this._cache.availableNames || Object.keys(this.blendShapeControls);
      const parts = ["head", "mouth", "beard", "moustache"];

      const it = this.visemeState.entries();
      for (let e = it.next(); !e.done; e = it.next()) {
        const phoneme = e.value[0],
          st = e.value[1];
        if (st.w <= 0) continue;

        // Collect targets once per viseme
        const targets = [];
        for (let i = 0; i < parts.length; i++) {
          const sub = this.mapper.mapPhonemeToBlendShapes(
            available,
            phoneme,
            parts[i],
          );
          for (let j = 0; j < sub.length; j++) {
            if (!targets.some((t) => t.name === sub[j].name))
              targets.push(sub[j]);
          }
        }

        for (let t = 0; t < targets.length; t++) {
          const name = targets[t].name,
            base = targets[t].weight;
          const raw = this.blendShapeControls[name];
          const first = Array.isArray(raw) ? raw[0] : raw;
          if (!first || !first.mesh || typeof first.index !== "number")
            continue;
          if (this.mapper.isProtectedEmotionShape(name)) continue;

          const finalW = Math.min(1, Math.max(0, base * st.w));
          const items = Array.isArray(raw) ? raw : [raw];
          for (const control of items) {
            if (control && control.mesh && typeof control.index === "number") {
              control.mesh.morphTargetInfluences[control.index] = finalW;
              control.value = finalW;
            }
          }
        }
      }
      return;
    }

    if (this.debug)
      console.warn("[LipSync] _renderVisemes(): no model/controls set");
  }
}

export { LipSyncController as MinimalLipSyncController };
